import React, { useState } from 'react';
import { X, Mail, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/apiBase';

const API = `${BACKEND_URL}/api`;

const ContactModal = ({ isOpen, onClose, variant = 'general', source = '' }) => {
  const isPro = variant === 'pro_request';
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const title = isPro ? 'Talk to a Professional' : 'Contact Support';
  const blurb = isPro
    ? "Tell us a bit about your business and what you need help with. We'll connect you with a professional."
    : 'Send us a message and our support team will get back to you by email.';

  const handleClose = () => {
    if (submitting) return;
    setName(''); setEmail(''); setMessage(''); setError(''); setSent(false);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError('Please fill in your name, email, and message.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
        message_type: isPro ? 'pro_request' : 'general',
        source: source || (isPro ? 'talk_to_pro' : 'contact'),
      });
      setSent(true);
    } catch (err) {
      setError('Something went wrong. Please try again, or email support@finance-vitals.com directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      data-testid="contact-modal-overlay"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full shadow-xl"
        data-testid="contact-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="contact-modal-close"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {sent ? (
            <div className="text-center py-6" data-testid="contact-success">
              <div className="bg-green-100 rounded-full p-3 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Message sent</h3>
              <p className="text-sm text-gray-600 mb-6">
                Thanks, {name.split(' ')[0] || 'there'}! We've received your message and will reply by email soon.
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                data-testid="contact-success-close"
              >
                Done
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
              <p className="text-sm text-gray-600">{blurb}</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="Jane Doe"
                  data-testid="contact-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  placeholder="you@example.com"
                  data-testid="contact-email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                <textarea
                  rows={4}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none resize-none"
                  placeholder={isPro ? 'What do you need help with? (e.g. bookkeeping cleanup, tax prep)' : 'How can we help?'}
                  data-testid="contact-message-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" data-testid="contact-error">{error}</p>
              )}

              <button
                type="submit"
                disabled={submitting}
                className={`w-full flex items-center justify-center px-6 py-3 rounded-lg font-semibold transition-colors ${
                  submitting ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
                data-testid="contact-submit"
              >
                {submitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2" /> Send message</>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Prefer email? Write to{' '}
                <a href="mailto:support@finance-vitals.com" className="text-blue-600 hover:underline">
                  support@finance-vitals.com
                </a>
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
